import { db } from '@/firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import type { JobEvent, JobEventType } from '@/types';
import { getSession } from '@/lib/session';

/**
 * Job event audit log.
 *
 * Records WHO did WHAT to a job and WHEN — who created it, and who
 * scheduled / moved / resized / reassigned / unscheduled it. Entries are
 * appended to `Job.eventLog` (mirrors the Deal.activityLog pattern).
 *
 * Prefer inlining `buildJobEvent()` into an existing `updateDoc` payload via
 * `arrayUnion(...)` so the event is written atomically with the change it
 * describes. Use the standalone `logJobEvent()` only when there is no other
 * write in flight.
 */

interface ActorOpts {
  /** userId of the acting user. Falls back to the current session. */
  actor?: string;
  /** Display name of the acting user. Falls back to the current session. */
  actorName?: string;
  /** Mark the event as system-generated rather than performed by a person. */
  automated?: boolean;
}

/**
 * Resolve the acting user from the session, with safe fallbacks.
 *
 * Reads through `getSession()` rather than raw localStorage (overhaul Wave 2)
 * — same keys, but guarded, so audit logging can't throw where `localStorage`
 * doesn't exist (unit tests, SSR-ish contexts).
 */
export function currentActor(): { actor: string; actorName: string } {
  const session = getSession();
  return {
    actor: session.userId || 'unknown',
    actorName: session.displayName || 'Office',
  };
}

/**
 * Build a JobEvent. Resolves the actor from the session unless one is passed
 * explicitly (services should pass it through — components can let it default).
 */
export function buildJobEvent(
  type: JobEventType,
  summary: string,
  opts: ActorOpts = {},
): JobEvent {
  const who = currentActor();
  return {
    id: crypto.randomUUID(),
    type,
    timestamp: new Date().toISOString(),
    actor: opts.actor ?? who.actor,
    actorName: opts.actorName ?? who.actorName,
    summary,
    automated: opts.automated ?? false,
  };
}

/**
 * Append a JobEvent to a job's eventLog as a standalone write.
 * Fire-and-forget friendly — swallows errors so audit logging can never
 * break the primary action.
 */
export async function logJobEvent(
  jobId: string,
  type: JobEventType,
  summary: string,
  opts: ActorOpts = {},
): Promise<void> {
  try {
    await updateDoc(doc(db, 'jobs', jobId), {
      eventLog: arrayUnion(buildJobEvent(type, summary, opts)),
    });
  } catch (err) {
    console.error('[job-event] failed to append', type, err);
  }
}
